import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Eye } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge, fmtMoney, fmtDate, ORDER_STATUSES } from "@/components/status";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = () => adminApi.allOrders("all").then(setOrders).catch((err) => { setOrders([]); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load orders"); });
  useEffect(() => { load(); }, []);

  const change = async (o, status) => {
    try { const d = await adminApi.updateOrderStatus(o.id, status); setOrders((l) => l.map((x) => (x.id === o.id ? d : x))); toast.success(`Status → ${status}`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const filtered = useMemo(() => orders.filter((o) => (filter === "all" || o.status === filter) && (o.id.toLowerCase().includes(search.toLowerCase()) || (o.store_code || "").toLowerCase().includes(search.toLowerCase()))), [orders, filter, search]);

  return (
    <div data-testid="admin-orders">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="font-serif text-3xl font-bold text-[#28282E]">Live Orders</h1><p className="text-sm text-[#28282E]/70">Manage incoming store orders.</p></div>
        <div className="flex flex-wrap gap-2">
          <div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#28282E]/55" /><Input data-testid="admin-orders-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search store / id…" className="w-56 pl-9" /></div>
          <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-40" data-testid="admin-orders-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem>{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 bg-[#FBEEF4] text-left text-[11px] font-semibold uppercase tracking-wide text-[#28282E]/55">
            <th className="px-4 py-3">Store</th><th className="px-4 py-3">Delivery</th><th className="px-4 py-3">Placed by</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Set</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {filtered.map((o) => (
              <tr key={o.id} data-testid={`admin-order-row-${o.id}`} className="border-b border-slate-50 hover:bg-[#FBEEF4]">
                <td className="px-4 py-3 font-semibold text-[#28282E]">{o.store_code}</td>
                <td className="px-4 py-3 text-[#28282E]/70">{o.delivery_estimate ? fmtDate(o.delivery_estimate) : "—"}</td>
                <td className="px-4 py-3 text-[#28282E]/70">{o.placed_by_name || "—"}</td>
                <td className="px-4 py-3 text-right font-medium">{fmtMoney(o.total)}</td>
                <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                <td className="px-4 py-3">
                  <Select value={ORDER_STATUSES.includes(o.status) ? o.status : undefined} onValueChange={(v) => change(o, v)}>
                    <SelectTrigger className="h-8 w-36" data-testid={`admin-order-status-select-${o.id}`}><SelectValue placeholder={o.status} /></SelectTrigger>
                    <SelectContent>{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3"><button data-testid={`admin-order-view-${o.id}`} onClick={() => setDetail(o)} className="grid h-8 w-8 place-items-center rounded-lg text-[#28282E]/55 hover:bg-[#FBEEF4] hover:text-[#28282E]"><Eye className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-16 text-center text-sm text-[#28282E]/55">No orders found.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-order-detail-modal">
          {detail && (<>
            <DialogHeader><DialogTitle className="font-serif">{detail.store_code}</DialogTitle></DialogHeader>
            <div className="mb-2 flex items-center gap-3 text-sm text-[#28282E]/70"><StatusBadge status={detail.status} /><span>{detail.delivery_estimate ? fmtDate(detail.delivery_estimate) : "no date"}</span></div>
            {detail.items.map((i, idx) => <div key={idx} className="flex justify-between border-t border-slate-100 py-1.5 text-sm"><span className="text-[#28282E]">{i.quantity}× {i.name}</span><span>{fmtMoney(i.price * i.quantity)}</span></div>)}
            <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 font-semibold"><span>Total</span><span>{fmtMoney(detail.total)}</span></div>
            {detail.delivery_notes ? <p className="mt-2 text-xs italic text-[#28282E]/70">Notes: {detail.delivery_notes}</p> : null}
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
